import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Send,
  X,
  Loader2,
  Bot,
  User,
  Minimize2,
  Maximize2,
  Zap,
  CheckCircle,
  AlertCircle,
  ExternalLink,
  Sparkles,
  RotateCcw,
  Paperclip,
  Image,
  File,
  Search,
  Database,
} from "lucide-react";
import Markdown from "react-markdown";
import { API } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";

function parseSSE(text) {
  const events = [];
  const lines = text.split("\n");
  let current = {};
  for (const line of lines) {
    if (line.startsWith("event: ")) {
      current.event = line.slice(7).trim();
    } else if (line.startsWith("data: ")) {
      try { current.data = JSON.parse(line.slice(6)); } catch { current.data = line.slice(6); }
    } else if (line.startsWith(":")) {
      // SSE comment (e.g., keepalive) — ignore
      continue;
    } else if (line === "" && current.event) {
      events.push({ ...current });
      current = {};
    }
  }
  // Flush remaining complete event not terminated by blank line
  if (current.event && current.data !== undefined) {
    events.push({ ...current });
  }
  return events;
}

export default function AskRexWidget({ clientContext = null }) {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [thinkingSteps, setThinkingSteps] = useState([]);
  const [pendingAction, setPendingAction] = useState(null);
  const [agentSessionId, setAgentSessionId] = useState(() => {
    const stored = localStorage.getItem("rex_agent_session_id");
    if (stored) return stored;
    const newId = `agent_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    localStorage.setItem("rex_agent_session_id", newId);
    return newId;
  });

  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, thinkingSteps]);

  useEffect(() => {
    // Tell floating siblings (Capture) whether the Rex panel is open so nothing overlaps it.
    window.dispatchEvent(new CustomEvent("rex:panel", { detail: { open: isOpen } }));
  }, [isOpen]);

  useEffect(() => {
    if (clientContext && !isOpen) {
      setIsOpen(true);
      setMessages([{
        role: "assistant",
        content: `I can see you're looking at **${clientContext.company_name}**. What would you like me to do?\n\nI can create a proposal, send an email, update their info, or schedule a follow-up.`,
        timestamp: new Date().toISOString()
      }]);
    }
  }, [clientContext]);

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 20 * 1024 * 1024) { toast.error("File too large. Max 20MB."); return; }
    const allowed = ["pdf", "docx", "xlsx", "xls", "png", "jpg", "jpeg", "webp", "heic", "gif", "txt", "csv", "md", "json", "xml", "html", "eml"];
    const ext = file.name.split(".").pop()?.toLowerCase();
    if (!allowed.includes(ext)) { toast.error(`Unsupported file type: .${ext}`); return; }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await fetch(`${API}/ask-rex/upload`, { method: "POST", credentials: "include", body: formData });
      if (!res.ok) { toast.error((await res.json()).detail || "Upload failed"); return; }
      const data = await res.json();
      setUploadedFile(data);
      toast.success(`${file.name} ready`);
      inputRef.current?.focus();
    } catch (err) { toast.error("Upload failed: " + err.message); }
    finally { setUploading(false); if (fileInputRef.current) fileInputRef.current.value = ""; }
  };

  const sendMessage = async () => {
    const userMessage = inputMessage.trim() || (uploadedFile ? `Analyse this file: ${uploadedFile.filename}` : "");
    if (!userMessage || sending) return;

    setInputMessage("");
    setSending(true);
    setThinkingSteps([]);
    // Clear any stale pending confirmation
    if (pendingAction) setPendingAction(null);

    const tempUserMsg = {
      role: "user",
      content: userMessage,
      timestamp: new Date().toISOString(),
      file: uploadedFile ? { filename: uploadedFile.filename, file_type: uploadedFile.file_type } : null,
    };
    setMessages(prev => [...prev, tempUserMsg]);

    const currentUpload = uploadedFile;
    setUploadedFile(null);

    const conversationHistory = messages.slice(-10).map(m => ({ role: m.role, content: m.content }));

    try {
      const response = await fetch(`${API}/ask-rex/agent/stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          instruction: userMessage,
          client_id: clientContext?.client_id || null,
          session_id: agentSessionId,
          conversation_history: conversationHistory,
          upload_id: currentUpload?.upload_id || null,
        }),
      });

      if (!response.ok) {
        // Fallback to non-streaming
        const fallbackRes = await fetch(`${API}/ask-rex/agent`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            instruction: userMessage,
            client_id: clientContext?.client_id || null,
            session_id: agentSessionId,
            conversation_history: conversationHistory,
            upload_id: currentUpload?.upload_id || null,
          }),
        });
        if (fallbackRes.ok) handleAgentResponse(await fallbackRes.json());
        else { toast.error("Failed to get response"); setMessages(prev => prev.slice(0, -1)); }
        return;
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let responseHandled = false;
      let eventsSeen = 0;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        // Filter out SSE keepalive comments before parsing
        buffer = buffer.replace(/^: keepalive\n\n/gm, "");
        const events = parseSSE(buffer);
        const lastNewline = buffer.lastIndexOf("\n\n");
        if (lastNewline !== -1) buffer = buffer.slice(lastNewline + 2);

        for (const evt of events) {
          eventsSeen += 1;
          if (evt.event === "status") {
            const msg = evt.data.message || (evt.data.step === "classified" ? "Understanding your request..." : `${evt.data.step}...`);
            setThinkingSteps(prev => [...prev, { type: "status", message: msg }]);
          } else if (evt.event === "tool_call") {
            setThinkingSteps(prev => [...prev, { type: "tool_call", message: evt.data.message || `Calling ${evt.data.tool}...` }]);
          } else if (evt.event === "tool_result") {
            setThinkingSteps(prev => [...prev, { type: "tool_result", message: evt.data.summary ? `Got: ${evt.data.summary.slice(0, 80)}` : `${evt.data.tool} complete` }]);
          } else if (evt.event === "response") {
            setThinkingSteps([]);
            handleAgentResponse(evt.data);
            responseHandled = true;
          } else if (evt.event === "done") {
            setThinkingSteps([]);
            if (!responseHandled) handleAgentResponse(evt.data);
            responseHandled = true;
          } else if (evt.event === "confirmation") {
            setThinkingSteps([]);
            handleAgentResponse(evt.data);
            responseHandled = true;
          } else if (evt.event === "error") {
            setThinkingSteps([]);
            toast.error(evt.data.error || "Something went wrong");
            responseHandled = true;
          }
        }
      }

      // Process any remaining buffer content after stream ends
      if (buffer.trim()) {
        const finalEvents = parseSSE(buffer);
        for (const evt of finalEvents) {
          if ((evt.event === "response" || evt.event === "done" || evt.event === "confirmation") && !responseHandled) {
            setThinkingSteps([]);
            handleAgentResponse(evt.data);
            responseHandled = true;
          }
        }
      }

      // If stream ended without a proper response, try the non-streaming
      // fallback before giving up — covers edge cases where a proxy buffers
      // or truncates SSE events. This was the "Rex took my text but did
      // nothing" symptom reported in Feb 2026.
      if (!responseHandled) {
        try {
          const fallbackRes = await fetch(`${API}/ask-rex/agent`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body: JSON.stringify({
              instruction: userMessage,
              client_id: clientContext?.client_id || null,
              session_id: agentSessionId,
              conversation_history: conversationHistory,
              upload_id: currentUpload?.upload_id || null,
            }),
          });
          if (fallbackRes.ok) {
            setThinkingSteps([]);
            handleAgentResponse(await fallbackRes.json());
            responseHandled = true;
          }
        } catch { /* ignore — handled by final fallback below */ }
      }

      // Last-resort fallback so the user is never left staring at a silent
      // chat — show an explanatory message and suggest a retry.
      if (!responseHandled) {
        setThinkingSteps([]);
        setMessages(prev => [...prev, {
          role: "assistant",
          content: eventsSeen === 0
            ? "I didn't get a response from the stream — this is usually a transient proxy hiccup. Please try again in a moment."
            : "I'm still processing your request — this may take a moment for complex operations. Please check back shortly or ask me again.",
          timestamp: new Date().toISOString(),
          type: "status",
        }]);
      }
    } catch (error) {
      toast.error("Failed to connect to Rex");
      setMessages(prev => prev.slice(0, -1));
    } finally {
      setSending(false);
      setThinkingSteps([]);
    }
  };

  const handleAgentResponse = (data) => {
    if (!data) return;
    const action = data.action;

    if (data.auto_execute && data.execution_result) {
      const result = data.execution_result;
      setMessages(prev => [...prev, {
        role: "assistant", content: data.response || result?.message || JSON.stringify(result, null, 2),
        timestamp: new Date().toISOString(), type: result?.success ? "success" : "query_result"
      }]);
      return;
    }

    if (action?.startsWith("QUERY_") && data.response) {
      setMessages(prev => [...prev, { role: "assistant", content: data.response, timestamp: new Date().toISOString(), type: "query" }]);
      return;
    }

    if (action === "NAVIGATE" && data.execution_result?.redirect_url) {
      setMessages(prev => [...prev, {
        role: "assistant", content: data.response || data.execution_result.message || "Navigating...",
        timestamp: new Date().toISOString(), type: "success", redirect_url: data.execution_result.redirect_url
      }]);
      navigate(data.execution_result.redirect_url);
      return;
    }

    if (action === "RESPOND" || action === "ERROR") {
      setMessages(prev => [...prev, {
        role: "assistant", content: data.response || data.error || "I'm not sure how to help with that.",
        timestamp: new Date().toISOString(), evidence: data.evidence || null,
      }]);
      return;
    }

    if (action === "CLARIFY") {
      const questions = data.questions?.join("\n") || data.message || "Could you provide more details?";
      setMessages(prev => [...prev, { role: "assistant", content: questions, timestamp: new Date().toISOString() }]);
      return;
    }

    if (data.requires_confirmation || !data.auto_execute) {
      // SYSTEM-WIDE INVARIANT: never render an approve/reject gate with nothing
      // to approve. Requires a real confirmation message OR concrete parameters.
      const params = data.params || data.parameters || {};
      const hasProposal = Boolean(
        (data.confirmation_message && String(data.confirmation_message).trim()) ||
        (params && Object.keys(params).length > 0)
      );
      if (!hasProposal) {
        const text = [data.confirmation_message, data.message, data.response]
          .find(v => typeof v === "string" && v.trim() && v.trim().toLowerCase() !== "undefined")
          || "I've processed your request.";
        setMessages(prev => [...prev, {
          role: "assistant", content: text, timestamp: new Date().toISOString(),
          evidence: data.evidence || null,
        }]);
        return;
      }
      setPendingAction({
        action, parameters: params,
        session_id: agentSessionId, task_id: data.task_id || null,
      });
      setMessages(prev => [...prev, {
        role: "assistant",
        content: data.confirmation_message || data.message || (
          action
            ? `I'm ready to ${String(action).replace(/_/g, ' ').toLowerCase()}. Shall I proceed?`
            : "Shall I proceed with this action?"
        ),
        timestamp: new Date().toISOString(), type: "confirmation",
      }]);
      return;
    }

    if (data.execution_result) {
      const result = data.execution_result;
      setMessages(prev => [...prev, {
        role: "assistant",
        content: result.success ? result.message : `Issue: ${result.error}`,
        timestamp: new Date().toISOString(), type: result.success ? "success" : "error",
        redirect_url: result.redirect_url,
      }]);
      return;
    }

    setMessages(prev => [...prev, {
      role: "assistant",
      content: data.message || data.confirmation_message || data.response || `I'll ${action?.toLowerCase()?.replace(/_/g, " ")} for you.`,
      timestamp: new Date().toISOString(),
    }]);
  };

  const executeConfirmedAction = async () => {
    if (!pendingAction) return;
    setSending(true);
    try {
      const response = await fetch(`${API}/ask-rex/agent/execute`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({
          action: pendingAction.action, parameters: pendingAction.parameters,
          session_id: pendingAction.session_id || agentSessionId, task_id: pendingAction.task_id || null,
        }),
      });
      if (response.ok) {
        const result = await response.json();
        const execResult = result.execution_result || result;
        const isSuccess = execResult.success || result.success;
        const msg = isSuccess ? (result.response || execResult.message || "Done") : (result.response || execResult.error || "Error");
        setMessages(prev => [...prev, {
          role: "assistant", content: msg, timestamp: new Date().toISOString(),
          type: isSuccess ? "success" : "error", redirect_url: execResult.redirect_url || result.redirect_url,
        }]);
        if (isSuccess && execResult.redirect_url) toast.success(execResult.message || msg);
      } else { toast.error("Failed to execute action"); }
    } catch { toast.error("Failed to execute action"); }
    finally { setPendingAction(null); setSending(false); }
  };

  const cancelPendingAction = () => {
    // Notify backend to clear pending confirmation from memory
    if (agentSessionId) {
      fetch(`${API}/ask-rex/agent/stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ instruction: "no, cancel that", session_id: agentSessionId }),
      }).catch(() => {}); // fire-and-forget
    }
    setPendingAction(null);
    setMessages(prev => [...prev, { role: "assistant", content: "No problem! Let me know what else you'd like to do.", timestamp: new Date().toISOString() }]);
  };

  const startNewChat = () => {
    setMessages([]);
    setPendingAction(null);
    setThinkingSteps([]);
    setUploadedFile(null);
    const newId = `agent_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    setAgentSessionId(newId);
    localStorage.setItem("rex_agent_session_id", newId);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey && !sending) { e.preventDefault(); sendMessage(); }
  };

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 right-6 w-14 h-14 bg-blue-600 hover:bg-blue-700 text-white rounded-full shadow-lg flex items-center justify-center transition-all hover:scale-110 z-50"
        data-testid="rex-widget-toggle"
      >
        <Bot className="w-6 h-6" />
      </button>
    );
  }

  return (
    <div
      className={`fixed bottom-6 right-6 bg-white rounded-xl shadow-2xl border border-slate-200 z-50 transition-all ${
        isMinimized ? "w-72 h-14" : "w-96 h-[520px]"
      }`}
      data-testid="rex-widget"
    >
      {/* Header */}
      <div className="flex items-center justify-between p-3 border-b border-slate-200 bg-blue-600 rounded-t-xl">
        <div className="flex items-center gap-2 text-white">
          <Bot className="w-5 h-5" />
          <span className="font-medium text-sm">Rex</span>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={startNewChat} className="p-1 text-white/60 hover:text-white transition-colors" title="New conversation" data-testid="rex-widget-new-chat">
            <RotateCcw className="w-4 h-4" />
          </button>
          <button onClick={() => setIsMinimized(!isMinimized)} className="p-1 text-white/60 hover:text-white transition-colors" data-testid="rex-widget-minimize">
            {isMinimized ? <Maximize2 className="w-4 h-4" /> : <Minimize2 className="w-4 h-4" />}
          </button>
          <button onClick={() => setIsOpen(false)} className="p-1 text-white/60 hover:text-white transition-colors" data-testid="rex-widget-close">
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {!isMinimized && (
        <>
          {/* Messages */}
          <ScrollArea className="flex-1 h-[calc(100%-112px)]">
            {messages.length === 0 && thinkingSteps.length === 0 ? (
              <div className="h-full flex items-center justify-center p-6">
                <div className="text-center">
                  <Bot className="w-10 h-10 text-blue-600 mx-auto mb-3" />
                  <p className="text-sm text-slate-600 font-medium">Ask Rex anything</p>
                  <p className="text-xs text-slate-400 mt-1">Upload files, query clients, or get guidance</p>
                </div>
              </div>
            ) : (
              <div className="p-3 space-y-3">
                {messages.map((msg, idx) => (
                  <div key={idx} className={`flex gap-2 ${msg.role === "user" ? "justify-end" : ""}`}>
                    {msg.role !== "user" && (
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${
                        msg.type === "confirmation" ? "bg-amber-500" : msg.type === "success" ? "bg-emerald-500" : msg.type === "error" ? "bg-red-500" : "bg-blue-600"
                      }`}>
                        <Bot className="w-3 h-3 text-white" />
                      </div>
                    )}
                    <div className={`max-w-[80%] rounded-lg px-3 py-2 ${
                      msg.role === "user"
                        ? "bg-blue-600 text-white text-sm"
                        : msg.type === "confirmation"
                        ? "bg-amber-50 text-slate-800 border border-amber-200 text-sm"
                        : msg.type === "success"
                        ? "bg-emerald-50 text-slate-800 border border-emerald-200 text-sm"
                        : msg.type === "error"
                        ? "bg-red-50 text-slate-800 border border-red-200 text-sm"
                        : "bg-slate-50 text-slate-800 border border-slate-200 text-sm"
                    }`}>
                      {msg.file && (
                        <div className="flex items-center gap-1 mb-1 text-[10px] opacity-80">
                          {["png","jpg","jpeg","webp","gif","heic"].includes(msg.file.file_type) ? <Image className="w-2.5 h-2.5" /> : <File className="w-2.5 h-2.5" />}
                          <span>{msg.file.filename}</span>
                        </div>
                      )}
                      {msg.role === "user" ? (
                        <span className="text-sm">{msg.content}</span>
                      ) : (
                        <div className="prose prose-sm prose-slate max-w-none [&>p]:mb-1 [&>p:last-child]:mb-0 [&>ul]:mb-1 [&>ol]:mb-1 [&>*]:leading-snug text-sm [&>h1]:text-sm [&>h2]:text-sm [&>h3]:text-xs">
                          <Markdown>{msg.content}</Markdown>
                        </div>
                      )}
                      {/* Confirmation buttons */}
                      {msg.type === "confirmation" && pendingAction && idx === messages.length - 1 && (
                        <div className="flex gap-2 mt-2 pt-2 border-t border-amber-200/60">
                          <Button size="sm" className="h-7 text-xs bg-blue-600 hover:bg-blue-700" onClick={executeConfirmedAction} disabled={sending} data-testid="widget-confirm-btn">
                            {sending ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle className="w-3 h-3 mr-1" />}
                            Confirm
                          </Button>
                          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={cancelPendingAction} disabled={sending} data-testid="widget-cancel-btn">
                            Cancel
                          </Button>
                        </div>
                      )}
                      {msg.redirect_url && (
                        <Button size="sm" variant="outline" className="mt-1.5 h-6 text-xs gap-1" onClick={() => navigate(msg.redirect_url)}>
                          <ExternalLink className="w-3 h-3" /> View
                        </Button>
                      )}
                    </div>
                    {msg.role === "user" && (
                      <div className="w-6 h-6 rounded-full bg-slate-700 flex items-center justify-center flex-shrink-0">
                        <User className="w-3 h-3 text-white" />
                      </div>
                    )}
                  </div>
                ))}

                {/* Thinking steps */}
                {thinkingSteps.length > 0 && (
                  <div className="flex gap-2" data-testid="widget-thinking-steps">
                    <div className="w-6 h-6 rounded-full bg-blue-600 flex items-center justify-center flex-shrink-0">
                      <Bot className="w-3 h-3 text-white" />
                    </div>
                    <div className="bg-slate-50 border border-slate-200 rounded-lg p-2 space-y-1 max-w-[80%]">
                      {thinkingSteps.map((step, i) => (
                        <div key={i} className="flex items-center gap-1.5 text-[10px] text-slate-500 animate-in fade-in duration-300">
                          {step.type === "status" && <Sparkles className="w-2.5 h-2.5 text-blue-500 flex-shrink-0" />}
                          {step.type === "tool_call" && <Search className="w-2.5 h-2.5 text-amber-500 flex-shrink-0" />}
                          {step.type === "tool_result" && <Database className="w-2.5 h-2.5 text-emerald-500 flex-shrink-0" />}
                          <span className={i === thinkingSteps.length - 1 ? "text-slate-700 font-medium" : "text-slate-400"}>
                            {step.message}
                          </span>
                          {i === thinkingSteps.length - 1 && <Loader2 className="w-2.5 h-2.5 animate-spin text-blue-500 ml-0.5" />}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {sending && thinkingSteps.length === 0 && (
                  <div className="flex gap-2">
                    <div className="w-6 h-6 rounded-full bg-blue-600 flex items-center justify-center flex-shrink-0">
                      <Bot className="w-3 h-3 text-white" />
                    </div>
                    <div className="bg-slate-50 border border-slate-200 rounded-lg p-2">
                      <Loader2 className="w-3.5 h-3.5 animate-spin text-blue-500" />
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>
            )}
          </ScrollArea>

          {/* Input area */}
          <div className="p-2.5 border-t border-slate-200">
            {uploadedFile && (
              <div className="flex items-center gap-1.5 mb-2 px-2 py-1.5 bg-blue-50 border border-blue-200 rounded-md text-[10px]" data-testid="widget-file-preview">
                {["png","jpg","jpeg","webp","gif","heic"].includes(uploadedFile.file_type) ? <Image className="w-3 h-3 text-blue-600" /> : <File className="w-3 h-3 text-blue-600" />}
                <span className="text-blue-800 font-medium truncate flex-1">{uploadedFile.filename}</span>
                <button onClick={() => setUploadedFile(null)} className="text-blue-400 hover:text-blue-600" data-testid="widget-remove-file">
                  <X className="w-3 h-3" />
                </button>
              </div>
            )}
            {uploading && (
              <div className="flex items-center gap-1.5 mb-2 px-2 py-1.5 bg-slate-50 border border-slate-200 rounded-md text-[10px] text-slate-500">
                <Loader2 className="w-3 h-3 animate-spin" />
                <span>Processing file...</span>
              </div>
            )}
            <div className="flex gap-1.5 items-center">
              <input type="file" ref={fileInputRef} onChange={handleFileUpload}
                accept=".pdf,.docx,.xlsx,.xls,.png,.jpg,.jpeg,.webp,.heic,.gif,.txt,.csv,.md,.json,.xml,.html,.eml,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,image/*,text/*"
                className="hidden" data-testid="widget-file-input" />
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={sending || uploading}
                className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors disabled:opacity-50"
                data-testid="widget-upload-btn"
                title="Upload file"
              >
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Paperclip className="w-4 h-4" />}
              </button>
              <Input
                ref={inputRef}
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder={uploadedFile ? `About ${uploadedFile.filename}...` : "Ask Rex..."}
                disabled={sending || !!pendingAction}
                className="flex-1 text-sm h-8"
                data-testid="rex-widget-input"
              />
              <Button
                onClick={sendMessage}
                disabled={(!inputMessage.trim() && !uploadedFile) || sending || !!pendingAction}
                size="sm"
                className="h-8 w-8 p-0 bg-blue-600 hover:bg-blue-700"
                data-testid="rex-widget-send"
              >
                {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
